// src/hooks/useFirstVisitUTM.ts

import axios from "axios";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Convert a snake_case string to camelCase.
 * Example: "utm_source" → "utmSource"
 */
function toCamelCase(s: string): string {
  return s.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Given an object whose keys are snake_case, return a new object
 * where each key is converted to camelCase. Values are preserved.
 */
function convertKeysToCamelCase(
  snakeObj: Record<string, string>
): Record<string, string> {
  const camelObj: Record<string, string> = {};
  Object.entries(snakeObj).forEach(([key, value]) => {
    const camelKey = toCamelCase(key);
    camelObj[camelKey] = value;
  });
  return camelObj;
}

export const useFirstVisitUTM = () => {
  const navigate = useNavigate(); // Initialize navigate
  useEffect(() => {
    const checkFirstVisit = async () => {
      if (window.location.pathname.toLowerCase().startsWith('/audiobook/')) return;
      const token = localStorage.getItem("token");
      if (token) return;

      const trackedKey = "utm_first_visit_tracked";

      // 1️⃣ If we've already set this flag, do nothing:
      const hasBeenTracked = localStorage.getItem(trackedKey);
      if (hasBeenTracked == "true") return;

      // 2️⃣ Otherwise, pull the raw UTM params (snake_case) from localStorage:
      const storedUTM: any = localStorage.getItem("utm_params");
      if (!storedUTM) return;

      let utmParamsSnake: Record<string, string>;
      try {
        utmParamsSnake = JSON.parse(storedUTM);
      } catch {
        // Invalid JSON → bail out
        return;
      }

      // 3️⃣ Convert snake_case keys → camelCase so they line up with the API response:

      const utmParams = convertKeysToCamelCase(utmParamsSnake);

      try {
        // 4️⃣ Fetch all registered UTM combinations via Axios:
        const response = await axios.get<{ utms: Array<any> }>(
          `${process.env.REACT_APP_REST_API_URI}/api/v1/shown/all-registered`
        );
        const registeredUTMs = response.data.utms;

        if (!Array.isArray(registeredUTMs) || registeredUTMs.length === 0)
          return;

        // 5️⃣ Look for the first matching UTM object:
        const matched = registeredUTMs.find((registered) =>
          Object.entries(utmParams).every(([camelKey, value]) => {
            if (camelKey === "bookTalk") return true; // Skip "bookTalk" from comparison
            return registered[camelKey] === value;
          })
        );

        if (matched) {
          // A pending campaign lookup must not redirect a visitor away from
          // an audiobook retailer landing page entered meanwhile.
          if (window.location.pathname.toLowerCase().startsWith('/audiobook/')) return;
          console.log(matched);

          // 6️⃣ We have a match! Extract the preSignupPrompt:
          const preSignupPrompt: string = matched.preSignupPrompt;
          if (preSignupPrompt.length == 0) return;
          localStorage.setItem(
            "allowed_presignup_prompt",
            matched.preSignupPrompt
          );

          // 7️⃣ Immediately set the flag so it won’t run again:
          localStorage.setItem(trackedKey, "true");

          // 8️⃣ Build the redirect URL with the prompt placeholder:
          const placeholderUrl = `/chat/${
            matched.character
          }?prompt=${encodeURIComponent(preSignupPrompt)}${
            utmParams.bookTalk ? `&bookTalk=${utmParams.bookTalk}` : ""
          }&send=true`;

          console.log(placeholderUrl);

          // 9️⃣ Navigate without refreshing:
          navigate(placeholderUrl);
        }
      } catch (error) {
        console.error("UTM tracking error:", error);
      }
    };

    setTimeout(() => {
      checkFirstVisit();
    }, 500);
  }, []);
};
